/**
 * NIIMBOT Print Helper v7
 * Integrates PDF.js rendering dengan NiimbotB4 driver.
 *
 * Fix v7:
 * - Canvas width dikunci ke 800px (sesuai MAX_WIDTH driver)
 * - Pastikan canvas tidak melampaui batas fisik printhead
 *
 * ─── KONFIGURASI UKURAN KERTAS ───────────────────────────────────────────────
 * Ubah nilai di bawah sesuai ukuran label fisik yang Anda gunakan.
 * Contoh umum:
 *   100×75mm  → PaperHeightMM: 75
 *   100×150mm → PaperHeightMM: 150
 *   100×100mm → PaperHeightMM: 100
 * ─────────────────────────────────────────────────────────────────────────────
 */

const PRINT_CONFIG = {
  DPI: 203,           // Resolusi printer NIIMBOT B4 (jangan diubah)
  PaperWidthMM: 100,  // Lebar kertas dalam mm (NIIMBOT B4 standar = 100mm)
  PaperHeightMM: 150, // ← UBAH INI sesuai tinggi label Anda (sekarang: 150mm)
  SafeMarginMM: 1     // Margin aman agar konten tidak terpotong di tepi
};

class PrintHelper {
  constructor() {
    this.pdfjs = null;
    this.isLibraryLoaded = false;
    console.log('PrintHelper constructor called');
  }

  async loadPdfJs() {
    if (this.isLibraryLoaded) return;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
      script.onload = () => {
        this.pdfjs = window['pdfjs-dist/build/pdf'];
        this.pdfjs.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
        this.isLibraryLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async showSelection(pdfBlobOrUrl) {
    console.log('PrintHelper.showSelection called');
    const swal = window.Swal || window.Sweetalert2;
    if (!swal) {
      console.error('SweetAlert2 not found! Falling back to normal print.');
      this.handleNormalPrint(pdfBlobOrUrl);
      return;
    }

    await swal.fire({
      title: 'Print Options',
      html: `
        <div class="alert alert-warning d-flex align-items-center mb-3" role="alert">
          <span class="alert-icon text-warning me-2">
            <i class="ti ti-bell ti-xs"></i>
          </span>
          <small>Pastikan Printer <b>SUDAH DI-UNPAIR</b> dari Windows Settings agar bisa ditemukan.</small>
        </div>
        <div class="d-flex flex-column gap-2 mt-2">
          <button id="btn-print-normal" class="btn btn-primary btn-lg w-100 py-3">
            <i class="ti ti-printer me-2 fs-3"></i> Normal Print (Standard)
          </button>
          <button id="btn-print-bluetooth" class="btn btn-info btn-lg w-100 py-3">
            <i class="ti ti-bluetooth me-2 fs-3"></i> Bluetooth Label (NIIMBOT B4)
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: () => {
        const content = swal.getHtmlContainer();
        content.querySelector('#btn-print-normal').onclick = () => {
          swal.close();
          this.handleNormalPrint(pdfBlobOrUrl);
        };
        content.querySelector('#btn-print-bluetooth').onclick = () => {
          swal.close();
          this.handleBluetoothPrint(pdfBlobOrUrl);
        };
      }
    });
  }

  handleNormalPrint(blobOrUrl) {
    console.log('Executing Normal Print');
    const url = typeof blobOrUrl === 'string' ? blobOrUrl : URL.createObjectURL(blobOrUrl);
    const newTab = window.open(url, '_blank');
    if (newTab) newTab.focus();
    else alert('Please allow popups to view PDF');
  }

  async renderPdfToCanvas(blobOrUrl) {
    const source = typeof blobOrUrl === 'string' ? blobOrUrl : URL.createObjectURL(blobOrUrl);
    const loadingTask = this.pdfjs.getDocument(source);
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const pixelsPerMM = PRINT_CONFIG.DPI / 25.4;

    // Kunci ke 800px agar sesuai dengan MAX_WIDTH di driver
    // (100mm × 203dpi / 25.4 = 799.2 → dibulatkan ke 800)
    const targetWidth  = 800;
    const targetHeight = Math.round(PRINT_CONFIG.PaperHeightMM * pixelsPerMM);
    const margin       = Math.round(PRINT_CONFIG.SafeMarginMM * pixelsPerMM);

    console.log(`[PrintHelper] Target canvas: ${targetWidth}x${targetHeight}px, margin: ${margin}px`);

    const viewport = page.getViewport({ scale: 1 });

    // "Contain" scale: pastikan seluruh PDF muat di dalam label
    const scale = Math.min(
      (targetWidth  - margin * 2) / viewport.width,
      (targetHeight - margin * 2) / viewport.height
    );
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width  = targetWidth;
    canvas.height = targetHeight;

    // Background putih solid — KRITIS agar pixel transparan tidak dicetak hitam
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Center konten di label
    const offsetX = (targetWidth  - scaledViewport.width)  / 2;
    const offsetY = (targetHeight - scaledViewport.height) / 2;
    context.translate(offsetX, offsetY);

    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;

    console.log(`[PrintHelper] PDF rendered at scale ${scale.toFixed(3)}, PDF size: ${scaledViewport.width.toFixed(0)}x${scaledViewport.height.toFixed(0)}px`);

    return canvas;
  }

  async handleBluetoothPrint(pdfBlobOrUrl) {
    console.log('Executing Bluetooth Print');
    const $ = window.jQuery || window.$;
    const swal = window.Swal || window.Sweetalert2;

    try {
      if (!$ || !$.blockUI) {
        throw new Error('Required libraries (jQuery/blockUI) not found');
      }

      $.blockUI({ message: 'Rendering PDF ke gambar...' });

      const startTime = performance.now();

      await this.loadPdfJs();

      console.time('PDF-Render');
      const canvas = await this.renderPdfToCanvas(pdfBlobOrUrl);
      console.timeEnd('PDF-Render');
      $.unblockUI();

      // Preview sebelum kirim ke printer
      const canvasDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const confirmPrint = await swal.fire({
        title: 'Preview Label',
        html: `
          <p class="mb-2 text-muted">
            PDF dirender ke ${canvas.width}&times;${canvas.height}px
            (${(canvas.width/8).toFixed(0)}&times;${(canvas.height/8).toFixed(0)}mm).
          </p>
          <div style="border: 1px solid #ccc; padding: 5px; max-height: 300px; overflow: auto; background: #ddd;">
            <img src="${canvasDataUrl}" style="width: 100%; height: auto;" />
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Kirim ke Printer',
        cancelButtonText: 'Batal'
      });

      if (!confirmPrint.isConfirmed) return;

      $.blockUI({ message: 'Menghubungkan ke Printer Bluetooth...' });
      await window.NiimbotB4.connect();

      const renderTime = ((performance.now() - startTime) / 1000).toFixed(2);
      $.blockUI({ message: `Mengirim gambar ke printer (render: ${renderTime}s)...` });

      await window.NiimbotB4.print(canvas);

      $.unblockUI();
      swal.fire('Berhasil', 'Label berhasil dikirim ke printer', 'success');

    } catch (error) {
      if ($ && $.unblockUI) $.unblockUI();
      console.error('BT Print Error:', error);
      if (swal) swal.fire('Error', error.message || 'Gagal print via Bluetooth', 'error');
      else alert('Error: ' + error.message);
    }
  }
}

(function () {
  const init = async () => {
    if (!window.PrintHelper) {
      window.PrintHelper = new PrintHelper();
      console.log('NIIMBOT Print Helper V7 Ready');
      if (window.NiimbotB4 && window.NiimbotB4.autoConnect) {
        window.NiimbotB4.autoConnect();
      }
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
